import { resetPassword, useAppDispatch } from "@vv/state";
import { ROUTES } from "@vv/types";
import { AuthFormCard, AuthShell, Button, PasswordInput, showToast } from "@vv/ui";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState, type FormEvent } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { getErrorMessage } from "../../lib/error-message";

export function ResetPasswordPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit = Boolean(token) && newPassword.length > 0 && passwordsMatch;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token) {
      showToast("Reset link is missing or invalid", "error");
      return;
    }
    if (!passwordsMatch) {
      showToast("Passwords do not match", "error");
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(resetPassword({ token, newPassword })).unwrap();
      showToast("Password reset — please sign in", "success");
      navigate(ROUTES.login, { replace: true });
    } catch (err) {
      showToast(getErrorMessage(err, "Could not reset password"), "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      <AuthFormCard
        title="Reset password"
        subtitle="Choose a new password for your account"
        footer={
          <Typography variant="body2" color="text.secondary" align="center">
            <Link component={RouterLink} to={ROUTES.login}>
              Back to sign in
            </Link>
          </Typography>
        }
      >
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          {!token ? (
            <Typography variant="body2" color="error">
              This reset link is missing its token. Please request a new one.
            </Typography>
          ) : null}
          <PasswordInput
            label="New password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
          <PasswordInput
            label="Confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={
              confirmPassword.length > 0 && !passwordsMatch ? "Passwords do not match" : " "
            }
            required
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            loading={submitting}
            disabled={!canSubmit}
          >
            Reset password
          </Button>
        </Stack>
      </AuthFormCard>
    </AuthShell>
  );
}
